// Tela de Perfis (issue #71) — nome + matriz Módulo × Nível de acesso. Só
// funciona na versão web (Permissão de Módulo é recurso do modo hospedado).

import type { PermissaoModuloEntrada } from "../../domain/perfis.ts";
import { permissoesDoPerfil } from "../../domain/perfis.ts";
import { MODULOS, NIVEIS_PERMISSAO, type Modulo, type NivelPermissao } from "../../domain/schema.ts";
import type { Perfil } from "../../domain/snapshot.ts";
import type { Store } from "../store.ts";
import { clear, el, tabelaPaginada, botao } from "../ui.ts";
import {
  abrirFormModal,
  barraCadastro,
  campo,
  campoObrigatorio,
  inputTexto,
  select,
  telaLista,
} from "./_cadastro.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo, SITUACAO_CADASTRO } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

const ROTULO_MODULO: Record<Modulo, string> = {
  operacional: "Operacional",
  financeiro: "Financeiro",
  estoque: "Estoque",
  configuracao: "Configuração",
};

const ROTULO_NIVEL: Record<NivelPermissao, string> = {
  nenhum: "Nenhum",
  leitura: "Leitura",
  gravacao: "Gravação",
  ambos: "Ambos",
};

function resumoPermissoes(permissoes: PermissaoModuloEntrada[]): string {
  const comAcesso = permissoes.filter((p) => p.nivel !== "nenhum");
  if (comAcesso.length === 0) return "Sem Acesso a Nenhum Módulo";
  return comAcesso
    .map((p) => `${ROTULO_MODULO[p.modulo]}: ${ROTULO_NIVEL[p.nivel]}`)
    .join(", ");
}

/** A matriz de 4 selects (um por Módulo), no padrão `campo()` das outras telas. */
function matrizPermissoes(atual: PermissaoModuloEntrada[]): {
  element: HTMLElement;
  valor(): PermissaoModuloEntrada[];
} {
  const porModulo = new Map(atual.map((p) => [p.modulo, p.nivel]));
  const linhas = MODULOS.map((modulo) => {
    const s = select(
      NIVEIS_PERMISSAO.map((n) => [n, ROTULO_NIVEL[n]] as const),
      porModulo.get(modulo) ?? "nenhum",
    );
    return { modulo, s };
  });
  const element = el(
    "div",
    { class: "matriz-permissoes" },
    ...linhas.map(({ modulo, s }) => campo(ROTULO_MODULO[modulo], s)),
  );
  return {
    element,
    valor: () => linhas.map(({ modulo, s }) => ({ modulo, nivel: s.value as NivelPermissao })),
  };
}

export function renderPerfis(container: HTMLElement, store: Store): () => void {
  if (!__HOSTED__) {
    clear(container);
    container.append(
      el(
        "p",
        { class: "muted lista-vazia" },
        "O cadastro de perfis só funciona na versão web (servidor).",
      ),
    );
    return () => {};
  }

  // filtros, busca e página num estado só (#93): null = todos
  const lista = new EstadoLista({ situacao: null as string[] | null });
  const busca = buscaDaLista(lista, "Buscar por nome…", () => render());
  const fSituacao = filtroMultiplo(lista, "situacao", "Situação", () => render());

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "configuracao");

    fSituacao.atualizar(SITUACAO_CADASTRO);
    const sitFiltro = fSituacao.selecionados();
    const perfis = [...snap.perfis]
      .filter((p) => sitFiltro.size === 0 || sitFiltro.has(p.ativo ? "ativo" : "inativo"))
      .filter((p) => busca.casa(p.nome))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    const barra = barraCadastro(
      [fSituacao.element],
      "Novo Perfil",
      () => abrirModal(null),
      () => {
        lista.limpar();
        render();
      },
      undefined,
      podeGravar,
    );

    if (perfis.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          el(
            "p",
            { class: "muted lista-vazia" },
            lista.mensagemVazio({ nenhum: "Nenhum perfil.", filtrado: "Nenhum perfil encontrado." }),
          ),
        ),
      );
      return;
    }

    const linhas = perfis.map((p) => {
      const acoes: Node[] = [];
      if (podeGravar) {
        const editar = botao("secundario", {}, "Editar");
        editar.addEventListener("click", () => abrirModal(p));
        const alternar = botao(
          "secundario",
          {},
          p.ativo ? "Inativar" : "Reativar",
        );
        alternar.addEventListener("click", () => {
          void store.executar(
            { agregado: "perfil", cmd: { tipo: "definirAtivo", id: p.id, ativo: !p.ativo } },
            p.ativo ? "Perfil inativado." : "Perfil reativado.",
          );
        });
        acoes.push(editar, alternar);
      }
      return el(
        "tr",
        { class: p.ativo ? "" : "inativo" },
        el("td", {}, p.nome),
        el("td", { class: "muted" }, resumoPermissoes(permissoesDoPerfil(snap, p.id))),
        el(
          "td",
          {},
          p.ativo
            ? el("span", { class: "estado-ativo" }, "Ativo")
            : el("span", { class: "estado-inativo" }, "Inativo"),
        ),
        el("td", { class: "acoes" }, ...acoes),
      );
    });

    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          "perfis",
          ["Nome", "Permissões", "Situação", ""],
          linhas,
          lista.tabela,
          render,
        ),
      ),
    );
  };

  const abrirModal = (atual: Perfil | null): void => {
    const nome = inputTexto("Nome do Perfil", atual?.nome ?? "");
    const matriz = matrizPermissoes(
      atual ? permissoesDoPerfil(store.get(), atual.id) : [],
    );

    abrirFormModal({
      titulo: atual ? `Editar ${atual.nome}` : "Novo Perfil",
      rotuloSalvar: atual ? "Salvar" : "Criar Perfil",
      largura: "largo",
      campos: () =>
        el(
          "div",
          {},
          campoObrigatorio("Nome", nome),
          campo("Permissão de Módulo", matriz.element),
        ),
      salvar: (aoErro) => {
        const permissoes = matriz.valor();
        const cmd = atual
          ? ({ tipo: "editar" as const, id: atual.id, nome: nome.value, permissoes })
          : ({ tipo: "criar" as const, nome: nome.value, permissoes });
        return store.executar({ agregado: "perfil", cmd }, undefined, aoErro);
      },
    });
  };

  render();
  return store.subscribe(render);
}
